import { inject, Injectable } from '@angular/core';
import { OldFrontendLocationStorageService } from '@app/shared/old-frontend/data-access';
import { isNotNull, isNotNullish } from '@fmnts/core';
import { isSameDay, parseISO } from '@fmnts/core/chronos';
import { selectAssignedCustomerId } from '@formunauts/shared/customer/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, of, skip, tap } from 'rxjs';
import {
  NextLocationApiActions,
  NextLocationCheckInOfflineActions,
  NextLocationInitActions,
} from './next-locations.actions';
import { selectCurrentLocationCheckIn } from './next-locations.selectors';

/**
 * Bridges the new frontend and the old frontend regarding the current location.
 */
@Injectable()
export class NextLocationsBridgeEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private ofLocationStorage = inject(OldFrontendLocationStorageService);
  /**
   * Hydrate the current location from location storage.
   */
  hydrateCurrentLocation$ = createEffect(() => {
    return of(this.ofLocationStorage.storedCheckIn).pipe(
      filter(isNotNullish),
      // Don't hydrate location checkIns that are not from today
      filter(({ timestamp }) => isSameDay(new Date(), parseISO(timestamp))),
      map((checkIn) => NextLocationInitActions.hydrate(checkIn)),
    );
  });

  /**
   * Persist current location to location storage.
   */
  persistCurrentLocation$ = createEffect(
    () => {
      return combineLatest([
        this.store.select(selectAssignedCustomerId).pipe(filter(isNotNull)),
        this.store.select(selectCurrentLocationCheckIn).pipe(
          // We're interested in changes
          skip(1),
        ),
      ]).pipe(
        tap(([customerId, checkIn]) => {
          if (!checkIn) {
            this.ofLocationStorage.clearStoredLocation();
          } else {
            this.ofLocationStorage.setStoredLocation({
              customer: customerId,
              id: checkIn.location.id,
              code: checkIn.location.code ?? '',
              name: checkIn.location.name ?? '',
              timestamp: checkIn.timestamp ?? '',
            });
          }
        }),
      );
    },
    { dispatch: false },
  );

  /**
   * Persists the location check in on failure to upload it
   * later via the offline store
   */
  offlineStoreCurrentLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationApiActions.checkIn.failure),
      map(({ checkIn }) =>
        NextLocationCheckInOfflineActions.persist({
          checkIn,
        }),
      ),
    );
  });
}
